import type { ClientHistoryEntry, ClientHistoryKind } from '@fos/shared/contracts/client-notes';
import type { ClientDto } from '@fos/shared/contracts/crm';
import { CLIENT_HISTORY_KIND_LABELS } from '@fos/shared/labels';
import { History } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { cn } from '../../lib/cn';
import { formatDateTimeShort, formatRelativePast } from '../../lib/format';
import { trpc } from '../../lib/trpc';
import { useAccess } from '../../lib/useAccess';
import { Avatar, Button, EmptyState, ErrorState, ListSkeleton, Section, Spinner, Status } from '../../ui';
import { StatusPill } from '../../ui/StatusPill';
import { NoteCard, NoteComposer } from './ClientNotes';
import { HISTORY_KIND_ICONS, recordHref, TASK_DONE_ICON } from './history-format';

/**
 * Zakładka „Historia” w profilu klienta (Fala 10): na górze „Dodaj notatkę…”, pod nią jedna oś czasu — notatki,
 * zmiany danych, maile, spotkania, zadania (utworzone / zamknięte), czas, akceptacje, przebiegi agentów i zdarzenia
 * (`history.clientTimeline`). Filtry po rodzaju (tylko rodzaje z modułów osoby), doładowywanie przy przewijaniu
 * (IntersectionObserver) z przyciskiem „Pokaż starsze” jako zapasem.
 */

const PAGE = 30;

function EntryIcon({ entry }: { entry: ClientHistoryEntry }) {
  const Icon = entry.type === 'task_done' ? TASK_DONE_ICON : HISTORY_KIND_ICONS[entry.kind];
  return (
    <span
      aria-hidden
      className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-surface text-fg-muted"
    >
      <Icon size={13} strokeWidth={1.75} />
    </span>
  );
}

function TimelineEntry({ entry, now }: { entry: ClientHistoryEntry; now: Date }) {
  if (entry.kind === 'note' && entry.note) {
    return (
      <li className="border-b border-line py-4 last:border-b-0">
        <NoteCard note={entry.note} />
      </li>
    );
  }
  const href = recordHref(entry.link);
  const who = [entry.actor?.name, entry.actorLabel].filter(Boolean).join(' · ');
  const title = href ? (
    <Link to={href} className="text-fg hover:text-accent hover:underline">
      {entry.title}
    </Link>
  ) : (
    entry.title
  );
  return (
    <li className="flex gap-3 border-b border-line py-3 last:border-b-0">
      {entry.actor ? (
        <Avatar initials={entry.actor.initials} color={entry.actor.avatarColor} title={entry.actor.name} />
      ) : entry.via === 'ai' || entry.via === 'mcp' ? (
        <Avatar ai initials="A" title={entry.actorLabel ?? 'AI'} />
      ) : (
        <EntryIcon entry={entry} />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-body-s break-words text-fg">{title}</p>
        {entry.detail && (
          <p className="mt-0.5 line-clamp-2 text-caption break-words text-fg-secondary">{entry.detail}</p>
        )}
        {entry.changes && entry.changes.length > 0 && (
          <ul className="mt-1 flex flex-col gap-0.5 text-caption text-fg-secondary">
            {entry.changes.map((c) => (
              <li key={c.field}>
                <span className="text-fg-muted">{c.label}: </span>
                {c.hidden ? (
                  'zmieniono'
                ) : (
                  <>
                    {c.from ?? '—'} → {c.to ?? '—'}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-fg-muted">
          <span>{CLIENT_HISTORY_KIND_LABELS[entry.kind]}</span>
          {who && <span>· {who}</span>}
          <time dateTime={new Date(entry.at).toISOString()} title={formatDateTimeShort(entry.at, now)}>
            · {formatRelativePast(entry.at, now)}
          </time>
          {entry.status && (
            <StatusPill tone={entry.status.tone} size="s">
              {entry.status.label}
            </StatusPill>
          )}
          {entry.simulated && <Status tone="warning">Symulacja</Status>}
        </p>
      </div>
    </li>
  );
}

/** Doładowanie następnej strony, gdy znacznik pojawi się w oknie (jsdom bez IntersectionObserver = tylko przycisk). */
function useLoadMore(onVisible: () => void, enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onVisible);
  cb.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) cb.current();
      },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);
  return ref;
}

/** Filtry rodzajów: „Wszystko” albo jeden rodzaj (tylko rodzaje dostępne osobie). */
function KindFilter({
  kinds,
  value,
  onChange,
}: {
  kinds: ClientHistoryKind[];
  value: ClientHistoryKind | null;
  onChange: (v: ClientHistoryKind | null) => void;
}) {
  const chip = (active: boolean) =>
    cn(
      'inline-flex h-7 shrink-0 items-center rounded-full border px-3 text-caption font-medium max-md:h-10',
      'focus-visible:outline-2 focus-visible:outline-accent',
      active ? 'border-fg bg-fg text-fg-inverse' : 'border-line bg-canvas text-fg-secondary hover:text-fg',
    );
  return (
    <fieldset className="-mx-4 flex min-w-0 gap-1.5 overflow-x-auto border-0 px-4 [scrollbar-width:none] md:mx-0 md:px-0">
      <legend className="sr-only">Rodzaj wpisów</legend>
      <button
        type="button"
        aria-pressed={value === null}
        className={chip(value === null)}
        onClick={() => onChange(null)}
      >
        Wszystko
      </button>
      {kinds.map((k) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          className={chip(value === k)}
          onClick={() => onChange(k)}
        >
          {CLIENT_HISTORY_KIND_LABELS[k]}
        </button>
      ))}
    </fieldset>
  );
}

export function ClientHistoryTimeline({ clientId, canAddNote }: { clientId: string; canAddNote: boolean }) {
  const [kind, setKind] = useState<ClientHistoryKind | null>(null);
  const query = trpc.history.clientTimeline.useInfiniteQuery(
    { clientId, limit: PAGE, ...(kind ? { kinds: [kind] } : {}) },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const kinds = query.data?.pages[0]?.kinds ?? [];
  const more = Boolean(query.hasNextPage);
  const sentinel = useLoadMore(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
  }, more);
  const now = new Date();

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      {canAddNote && (
        <Section aria-label="Dodaj notatkę">
          <NoteComposer clientId={clientId} />
        </Section>
      )}
      <Section aria-labelledby="client-history-timeline">
        <div className="flex flex-col gap-3">
          <h2 id="client-history-timeline" className="text-body-s font-semibold text-fg">
            Historia klienta
          </h2>
          {kinds.length > 0 && <KindFilter kinds={kinds} value={kind} onChange={setKind} />}
        </div>
        <div className="mt-2">
          {query.isPending ? (
            <ListSkeleton rows={5} />
          ) : query.isError ? (
            <ErrorState
              compact
              title="Nie udało się wczytać historii"
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
            />
          ) : items.length === 0 ? (
            <EmptyState
              compact
              icon={History}
              title={kind ? 'Brak wpisów tego rodzaju.' : 'Historia jest pusta.'}
              description="Notatki, zmiany, maile, spotkania, zadania i czas pojawią się tutaj."
            />
          ) : (
            <>
              <ol aria-label="Oś czasu klienta" className="flex flex-col">
                {items.map((e) => (
                  <TimelineEntry key={e.key} entry={e} now={now} />
                ))}
              </ol>
              <div ref={sentinel} />
              {more && (
                <div className="mt-3 flex justify-center">
                  <Button
                    variant="ghost"
                    size="s"
                    onClick={() => void query.fetchNextPage()}
                    loading={query.isFetchingNextPage}
                  >
                    Pokaż starsze
                  </Button>
                </div>
              )}
              {query.isFetchingNextPage && !more && <Spinner size={14} />}
            </>
          )}
        </div>
      </Section>
    </div>
  );
}

/** Zakładka „Historia” (slot profilu klienta `setClientTab('history', …)`). */
export function ClientHistoryTab({ client }: { client: ClientDto }) {
  const access = useAccess();
  return <ClientHistoryTimeline clientId={client.id} canAddNote={access.can('clients', 'edit') && client.canEdit} />;
}
